import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { ArrowCounterClockwise, ArrowClockwise, ArrowsOut } from "@phosphor-icons/react";

/**
 * LogoCropModal — lightweight in-app crop/rotate tool for the company
 * logo, so operators don't have to bounce out to Photoshop for a tweak.
 *
 * The canvas shows the source image inside a fixed square viewport
 * (``VIEW_SIZE``). The operator can:
 *   - drag the image to reposition inside the viewport
 *   - scale it via the Zoom slider
 *   - rotate it via the Rotation slider or ±90° buttons
 *
 * On Save we render the viewport contents to an off-screen canvas at
 * ``EXPORT_SIZE`` and return a PNG data URL to the parent, which then
 * runs it through ``autoPadLogo`` and persists.
 */
const VIEW_SIZE = 320;   // On-screen preview square (px)
const EXPORT_SIZE = 512; // Exported canvas resolution (px)

const LogoCropModal = ({ open, sourceUrl, onCancel, onSave }) => {
  const [img, setImg] = useState(null);
  const [scale, setScale] = useState(1);      // multiplier over the natural fit
  const [rotation, setRotation] = useState(0); // deg
  const [pos, setPos] = useState({ x: 0, y: 0 }); // pan offset in export coords
  const dragRef = useRef(null);
  const canvasRef = useRef(null);

  // Load the source image whenever the modal opens.
  useEffect(() => {
    if (!open || !sourceUrl) return undefined;
    setScale(1);
    setRotation(0);
    setPos({ x: 0, y: 0 });
    const el = new Image();
    el.onload = () => setImg(el);
    el.onerror = () => setImg(null);
    el.src = sourceUrl;
    return () => { el.onload = null; el.onerror = null; };
  }, [open, sourceUrl]);

  // Redraw the preview whenever transform state changes.
  useEffect(() => {
    if (!img || !canvasRef.current) return;
    _drawPreview(canvasRef.current, img, { scale, rotation, pos });
  }, [img, scale, rotation, pos]);

  const onPointerDown = (e) => {
    dragRef.current = { x: e.clientX, y: e.clientY, base: { ...pos } };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    // Preview is VIEW_SIZE-square; scale motion into export coord space so
    // dragging feels 1:1 with what will be exported.
    const factor = EXPORT_SIZE / VIEW_SIZE;
    setPos({
      x: dragRef.current.base.x + (e.clientX - dragRef.current.x) * factor,
      y: dragRef.current.base.y + (e.clientY - dragRef.current.y) * factor,
    });
  };
  const onPointerUp = () => { dragRef.current = null; };

  const bumpRotation = (delta) => setRotation((r) => (r + delta + 360) % 360);
  const reset = () => { setScale(1); setRotation(0); setPos({ x: 0, y: 0 }); };

  const handleSave = () => {
    if (!img) return;
    const out = document.createElement("canvas");
    out.width = EXPORT_SIZE;
    out.height = EXPORT_SIZE;
    _drawExport(out, img, { scale, rotation, pos });
    onSave(out.toDataURL("image/png"));
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="sm:max-w-md" data-testid="logo-crop-modal">
        <DialogHeader>
          <DialogTitle>Crop &amp; Rotate Logo</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col items-center gap-3">
          <div
            className="rounded-lg border bg-muted overflow-hidden touch-none cursor-grab active:cursor-grabbing"
            style={{ width: VIEW_SIZE, height: VIEW_SIZE }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            data-testid="logo-crop-viewport"
          >
            <canvas
              ref={canvasRef}
              width={VIEW_SIZE}
              height={VIEW_SIZE}
              className="block w-full h-full select-none pointer-events-none"
            />
          </div>
          <p className="text-[11px] text-muted-foreground text-center">
            Drag to reposition. Use the sliders or buttons to zoom &amp; rotate.
          </p>

          <div className="w-full space-y-3">
            <div className="space-y-1">
              <Label className="text-xs flex items-center justify-between">
                <span className="flex items-center gap-1"><ArrowsOut size={12} /> Zoom</span>
                <span className="font-mono text-muted-foreground">{scale.toFixed(2)}×</span>
              </Label>
              <Slider
                value={[scale]}
                min={0.3}
                max={3}
                step={0.01}
                onValueChange={([v]) => setScale(v)}
                data-testid="logo-crop-zoom"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs flex items-center justify-between">
                <span>Rotation</span>
                <span className="font-mono text-muted-foreground">{Math.round(rotation)}°</span>
              </Label>
              <Slider
                value={[rotation]}
                min={0}
                max={360}
                step={1}
                onValueChange={([v]) => setRotation(v)}
                data-testid="logo-crop-rotation"
              />
              <div className="flex gap-2 justify-center pt-1">
                <Button size="sm" variant="outline" onClick={() => bumpRotation(-90)} data-testid="logo-crop-rotate-ccw">
                  <ArrowCounterClockwise size={14} className="mr-1" /> 90°
                </Button>
                <Button size="sm" variant="outline" onClick={() => bumpRotation(90)} data-testid="logo-crop-rotate-cw">
                  <ArrowClockwise size={14} className="mr-1" /> 90°
                </Button>
                <Button size="sm" variant="ghost" onClick={reset} data-testid="logo-crop-reset">Reset</Button>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="logo-crop-cancel">Cancel</Button>
          <Button onClick={handleSave} disabled={!img} data-testid="logo-crop-save">Save Logo</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

function _drawInto(ctx, size, img, { scale, rotation, pos }) {
  // Neutral surround so cropping a rectangular photo yields a square
  // asset the rest of the UI can rely on (matches the on-screen chrome).
  ctx.clearRect(0, 0, size, size);
  ctx.save();
  ctx.translate(size / 2 + pos.x * (size / EXPORT_SIZE), size / 2 + pos.y * (size / EXPORT_SIZE));
  ctx.rotate((rotation * Math.PI) / 180);
  // Fit the image so its longer edge covers the viewport at scale=1.
  const base = Math.min(size / img.width, size / img.height);
  const s = base * scale;
  const w = img.width * s;
  const h = img.height * s;
  ctx.drawImage(img, -w / 2, -h / 2, w, h);
  ctx.restore();
}

function _drawPreview(canvas, img, transform) {
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, VIEW_SIZE, VIEW_SIZE);
  _drawInto(ctx, VIEW_SIZE, img, transform);
}

function _drawExport(canvas, img, transform) {
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, EXPORT_SIZE, EXPORT_SIZE);
  _drawInto(ctx, EXPORT_SIZE, img, transform);
}

export default LogoCropModal;
